import React, { useState } from 'react'
import * as THREE from 'three'
import { ItemProps } from '@/components/Form/types'
import Button from '@/components/UiElements/Button'
import {
  DEFAULT_MAX_ANGLE_DEG,
  MM_PER_UNIT,
  SectionPlacement,
  frameFromSection,
  frameRoll,
  fromVector3,
  orthonormalise,
  suggestWrapRadius,
  toVector3,
  withRoll,
} from '@/shared/sectionFrames'

/**
 * Where a section sits, for `section_model: 'frame'` profiles.
 *
 * This replaces Radial Angle and Offset From Bottom, which describe a position on an
 * unrolled cylinder and have no meaning on a shape that is not round. A frame is placed by
 * clicking the model instead: the raycast gives the point and the surface normal, and the
 * remaining controls shape the projection around them.
 *
 * Rendered as a single `Preview` form item rather than five declarative fields because the
 * controls are interdependent — rotation is derived from `up` and the normal together, and
 * the wrap suggestion needs the mesh — and because placing is an action on the 3D view, not
 * a value typed into a box.
 */

const DEFAULTS = { depth: 30, wrapRadius: 0, maxAngleDeg: DEFAULT_MAX_ANGLE_DEG }

const Row: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="flex flex-col gap-xxs">
    <label className="text-text-xs text-gray-500">{label}</label>
    {children}
    {hint && <span className="text-text-xs text-gray-400">{hint}</span>}
  </div>
)

const Slider: React.FC<{ value: number; min: number; max: number; step?: number; onChange: (v: number) => void }> = ({
  value,
  min,
  max,
  step = 1,
  onChange,
}) => (
  <input
    type="range"
    className="w-full accent-primary-500"
    min={min}
    max={max}
    step={step}
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value))}
  />
)

const SectionFramePlacement: React.FC<ItemProps> = (props) => {
  const { formData, onChange } = props
  const [placing, setPlacing] = useState(false)
  // Nudge step, in millimetres across the surface.
  const [step, setStep] = useState(1)
  const [error, setError] = useState<string | null>(null)

  const placement: SectionPlacement | null = formData?.placement ?? null
  const meshName: string | undefined = formData?.targetObject?.name

  /**
   * Shift the section within its own plane.
   *
   * Along the frame's right and up vectors rather than world X/Y/Z, because those only
   * line up with the surface by coincidence: on a frame facing -X, changing X pushes the
   * section into the part instead of sliding it along the face. The absolute position is
   * still editable below for when an exact coordinate is what you want.
   */
  const nudge = (acrossMm: number, upMm: number) => {
    if (!placement) return
    const basis = orthonormalise(toVector3(placement.normal), toVector3(placement.up))
    const moved = toVector3(placement.origin)
      .add(basis.right.clone().multiplyScalar(acrossMm / MM_PER_UNIT))
      .add(basis.up.clone().multiplyScalar(upMm / MM_PER_UNIT))
    onChange?.('placement', { ...placement, origin: fromVector3(moved) })
  }

  const set = (next: Partial<SectionPlacement>) => {
    if (!placement) return
    onChange?.('placement', { ...placement, ...next })
  }

  /**
   * Place or re-place the section by clicking the model.
   *
   * Reuses the viewer's existing pick so hovering highlights the same way it does when
   * choosing a target object — the operator learns one interaction, not two.
   */
  const pickOnModel = async () => {
    const viewer = (window as any).threeViewer
    if (!viewer?.getTargetObject) return

    setPlacing(true)
    setError(null)
    try {
      const obj = await viewer.getTargetObject()
      const hit = viewer.getLastPickHit?.()
      if (!obj || !hit) {
        setError('That click did not land on a surface facing you. Rotate the model and try again.')
        return
      }

      const root: THREE.Object3D = viewer.product
      root.updateMatrixWorld(true)
      const inv = new THREE.Matrix4().copy(root.matrixWorld).invert()

      const origin = hit.point.clone().applyMatrix4(inv)
      const normal = hit.normal.clone().transformDirection(inv).normalize()
      // World +Y as the up hint: artwork on a grip, a flat face or a bottle wall all
      // want the same way up, and only the surface they sit on differs.
      const { up } = orthonormalise(normal, new THREE.Vector3(0, 1, 0))

      onChange?.('placement', {
        ...DEFAULTS,
        ...(placement ?? {}),
        origin: fromVector3(origin),
        normal: fromVector3(normal),
        up: fromVector3(up),
      })
      onChange?.('targetObject', { ...(formData?.targetObject ?? {}), name: hit.object.name })
    } finally {
      setPlacing(false)
    }
  }

  if (!placement) {
    // Reached only if the pick returned no front-facing surface — a click that went
    // through a gap and hit the inside of a far wall, which is not somewhere a section
    // can be engraved.
    return (
      <div className="flex flex-col gap-sm">
        <Button onClick={pickOnModel} type="alt" icon="" className="!justify-start !flex-row gap-md">
          <span>{placing ? 'Click the model…' : 'Place on model'}</span>
        </Button>
        {error && <span className="text-text-xs text-error-700">{error}</span>}
        <span className="text-text-xs text-gray-400">
          Choosing the target object normally places the section too. Use this if the spot needs changing, or if the
          first click did not land on a surface facing you.
        </span>
      </div>
    )
  }

  // The frame as the renderer sees it, so the readouts match what is drawn.
  const frame = frameFromSection({ ...formData, targetObject: { name: meshName, placement } })
  const roll = frame ? frameRoll(frame) : 0

  let suggested: number | null = null
  const viewer = (window as any).threeViewer
  if (frame && viewer?.product && meshName) {
    const mesh = viewer.product.getObjectByName(meshName)
    if (mesh) suggested = suggestWrapRadius(frame, mesh, viewer.product)
  }

  const originMm = placement.origin.map((v) => v * MM_PER_UNIT)

  return (
    <div className="flex flex-col gap-md">
      <Button onClick={pickOnModel} type="alt" icon="" className="!justify-start !flex-row gap-md">
        <span>{placing ? 'Click the model…' : 'Move to a new spot'}</span>
      </Button>
      {error && <span className="text-text-xs text-error-700">{error}</span>}

      <Row label={`Rotation — ${Math.round(roll)}°`} hint="Turns the section in its own plane.">
        <Slider
          value={roll}
          min={-180}
          max={180}
          onChange={(deg) => {
            if (!frame) return
            set({ up: withRoll(frame, deg).up })
          }}
        />
      </Row>

      <Row
        label={`Projection depth — ${Math.round(placement.depth)} mm`}
        hint="How far the section reaches into the part. Lower it if artwork appears on the far side."
      >
        <Slider value={placement.depth} min={1} max={200} onChange={(depth) => set({ depth })} />
      </Row>

      <Row
        label={`Wrap radius — ${placement.wrapRadius > 0 ? `${Math.round(placement.wrapRadius)} mm` : 'flat'}`}
        hint="0 is a flat decal. Set it to the surface radius to wrap around a curve."
      >
        <Slider value={placement.wrapRadius} min={0} max={300} onChange={(wrapRadius) => set({ wrapRadius })} />
        {suggested !== null && suggested > 0 && suggested < 400 && (
          <button
            type="button"
            className="self-start text-text-xs text-primary-600 hover:underline"
            onClick={() => set({ wrapRadius: Math.round(suggested as number) })}
          >
            Use surface radius ({Math.round(suggested)} mm)
          </button>
        )}
      </Row>

      <Row
        label={`Surface angle limit — ${Math.round(placement.maxAngleDeg)}°`}
        hint="Where the surface turns further away than this, the section stops rather than stretching down the side."
      >
        <Slider value={placement.maxAngleDeg} min={10} max={89} onChange={(maxAngleDeg) => set({ maxAngleDeg })} />
      </Row>

      <div className="flex flex-col gap-xxs">
        <span className="text-text-xs text-gray-500">Nudge on the surface</span>
        <div className="flex items-center gap-xs">
          {(
            [
              ['◀', -1, 0],
              ['▶', 1, 0],
              ['▲', 0, 1],
              ['▼', 0, -1],
            ] as const
          ).map(([glyph, dx, dy]) => (
            <button
              key={glyph}
              type="button"
              onClick={() => nudge(dx * step, dy * step)}
              className="w-[34px] h-[34px] rounded-md border border-gray-300 bg-white text-gray-700 hover:border-primary-500 transition-all"
            >
              {glyph}
            </button>
          ))}
          <div className="relative flex-1">
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={step}
              onChange={(e) => {
                const v = parseFloat(e.target.value)
                if (!Number.isNaN(v) && v > 0) setStep(v)
              }}
              className="w-full h-[34px] px-sm pr-[34px] rounded-md border border-gray-300 bg-white text-text-md text-gray-900 focus:border-primary-500 focus:shadow-focus outline-none"
            />
            <span className="absolute right-sm top-1/2 -translate-y-1/2 text-text-xs text-gray-400 pointer-events-none">
              mm
            </span>
          </div>
        </div>
        <span className="text-text-xs text-gray-400">
          Moves the section across its own face, so it stays on the surface whichever way that face points.
        </span>

        <div className="h-px bg-gray-200 my-xs" />

        <span className="text-text-xs text-gray-500">Position (mm)</span>
        <div className="grid grid-cols-3 gap-xs">
          {(['X', 'Y', 'Z'] as const).map((axis, i) => (
            <div key={axis} className="relative">
              <input
                type="number"
                step={0.5}
                value={Math.round(originMm[i] * 10) / 10}
                onChange={(e) => {
                  const mm = parseFloat(e.target.value)
                  if (Number.isNaN(mm)) return
                  const next = [...placement.origin] as [number, number, number]
                  next[i] = mm / MM_PER_UNIT
                  set({ origin: next })
                }}
                className="w-full h-[34px] px-sm pr-[22px] rounded-md border border-gray-300 bg-white text-text-md text-gray-900 focus:border-primary-500 focus:shadow-focus outline-none"
              />
              <span className="absolute right-xs top-1/2 -translate-y-1/2 text-text-xs text-gray-400 pointer-events-none">
                {axis}
              </span>
            </div>
          ))}
        </div>
        <span className="text-text-xs text-gray-400 tabular-nums">
          Facing{' '}
          {toVector3(placement.normal)
            .toArray()
            .map((v) => v.toFixed(2))
            .join(', ')}
        </span>
      </div>
    </div>
  )
}

export default SectionFramePlacement
